import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Button, Input, VStack, Heading, Text, Image, Divider, useToast, InputGroup, InputLeftElement } from '@chakra-ui/react';
import { motion } from 'framer-motion';
import { FaUser, FaPhone, FaCheck } from 'react-icons/fa';
import { supabase } from '../../services/supabaseClient';
import LogoBarber from '../../assets/Logo-Barber.png';

const MotionBox = motion(Box);

const MobileCheckinPage = () => {
  const [nome, setNome] = useState('');
  const [telefone, setTelefone] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();
  const toast = useToast();

  const handleCheckin = async () => {
    setError('');
    if (!nome.trim()) {
      setError('Por favor, preencha o nome.');
      return;
    }
    
    // Validar telefone
    if (!telefone.trim()) {
      setError('Por favor, preencha o telefone.');
      return;
    }
    
    // Validar formato do telefone (pelo menos 10 dígitos)
    const phoneDigits = telefone.replace(/\D/g, '');
    if (phoneDigits.length < 10 || phoneDigits.length > 11) {
      setError('Por favor, informe um número de telefone válido com DDD (10 ou 11 dígitos).');
      return;
    }
    
    setLoading(true);

    try {
      // 1. Salva o atendimento no Supabase
      const { data: atendimento, error: supaError } = await supabase
        .from('atendimentos')
        .insert([
          {
            nome_cliente: nome,
            telefone: telefone,
            mensalista: false,
            barbeiro: 'Não definido',
            status: 'mobile_pending', // Status especial para check-in via mobile
            iniciado_em: new Date().toISOString(),
          },
        ])
        .select()
        .single();

      if (supaError) throw supaError;

      // 2. Notifica o totem sobre o novo check-in via canal de realtime
      await supabase
        .channel('mobile-checkin')
        .send({
          type: 'broadcast',
          event: 'mobile-checkin',
          payload: { 
            atendimentoId: atendimento.id,
            nome: nome,
            telefone: telefone
          }
        });

      toast({
        title: "Check-in realizado!",
        description: "Você será chamado em breve.",
        status: "success",
        duration: 3000,
        isClosable: true,
        position: "top"
      });
      
      navigate('/mobile/welcome');
    } catch (error) {
      console.error('Erro no check-in:', error);
      setError('Erro ao realizar check-in. Tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <MotionBox 
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.5 }}
      minH="100vh" 
      bg="black" 
      color="white" 
      display="flex" 
      flexDirection="column"
      alignItems="center" 
      justifyContent="center"
      bgGradient="linear(to-b, gray.900, black)"
      px={4}
      py={8}
    >
      <MotionBox
        initial={{ y: -20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ delay: 0.2, duration: 0.5 }}
        mb={8}
      >
        <Image 
          src={LogoBarber} 
          alt="Logo Barbearia Freire" 
          maxW="150px" 
          mx="auto"
          filter="drop-shadow(0 4px 8px rgba(0, 0, 0, 0.5))"
        />
      </MotionBox>

      <MotionBox
        initial={{ y: 20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ delay: 0.4, duration: 0.5 }}
        w="100%" 
        maxW="400px" 
        bg="gray.800" 
        p={8} 
        borderRadius="2xl" 
        boxShadow="0 8px 32px rgba(0, 0, 0, 0.5)"
        border="1px solid"
        borderColor="whiteAlpha.200"
        backdropFilter="blur(10px)"
      >
        <VStack spacing={6} align="stretch">
          <Box textAlign="center">
            <Heading size="lg" color="yellow.400" mb={2}>Check-in</Heading>
            <Text color="whiteAlpha.700">Preencha seus dados para iniciar seu atendimento</Text>
          </Box>

          <Divider borderColor="whiteAlpha.200" />

          <VStack spacing={4}>
            <InputGroup>
              <InputLeftElement pointerEvents="none" children={<FaUser color="#ECC94B" />} h="50px" pl={3} />
              <Input
                placeholder="Seu nome completo"
                size="lg"
                height="50px"
                value={nome}
                onChange={(e) => setNome(e.target.value)}
                bg="whiteAlpha.100"
                border="1px solid"
                borderColor="whiteAlpha.300"
                _hover={{ borderColor: 'yellow.400' }}
                _focus={{ borderColor: 'yellow.400', boxShadow: '0 0 0 1px #ECC94B' }}
                pl={10}
                fontSize="md"
                borderRadius="xl"
              />
            </InputGroup>

            <InputGroup>
              <InputLeftElement pointerEvents="none" children={<FaPhone color="#ECC94B" />} h="50px" pl={3} />
              <Input
                placeholder="(00) 00000-0000"
                size="lg"
                height="50px"
                value={telefone}
                onChange={(e) => {
                  // Obter apenas os números do input
                  const numbers = e.target.value.replace(/\D/g, '');
                  
                  // Aplicar máscara de telefone brasileiro
                  let formatted = '';
                  
                  if (numbers.length <= 2) {
                    // Apenas DDD
                    formatted = numbers.length ? `(${numbers}` : '';
                  } else if (numbers.length <= 7) {
                    // DDD + parte do número
                    formatted = `(${numbers.substring(0, 2)}) ${numbers.substring(2)}`;
                  } else if (numbers.length <= 11) {
                    // Número completo
                    formatted = `(${numbers.substring(0, 2)}) ${numbers.substring(2, 7)}-${numbers.substring(7)}`;
                  } else {
                    // Limitar a 11 dígitos (DDD + 9 dígitos)
                    formatted = `(${numbers.substring(0, 2)}) ${numbers.substring(2, 7)}-${numbers.substring(7, 11)}`;
                  }
                  
                  setTelefone(formatted);
                }}
                bg="whiteAlpha.100"
                border="1px solid"
                borderColor="whiteAlpha.300"
                _hover={{ borderColor: 'yellow.400' }}
                _focus={{ borderColor: 'yellow.400', boxShadow: '0 0 0 1px #ECC94B' }}
                pl={10}
                fontSize="md"
                borderRadius="xl"
              />
            </InputGroup>

            {error && (
              <Text color="red.300" fontSize="sm" fontWeight="medium" p={2} bg="red.900" borderRadius="md" opacity={0.9}>
                {error}
              </Text>
            )}
          </VStack>

          <Button
            colorScheme="yellow"
            size="lg"
            width="100%"
            isLoading={loading}
            onClick={handleCheckin}
            height="56px"
            borderRadius="xl"
            fontWeight="bold"
            fontSize="lg"
            leftIcon={<FaCheck />}
            _hover={{ transform: 'translateY(-2px)', boxShadow: 'lg' }}
            _active={{ transform: 'translateY(0)', boxShadow: 'md' }}
            transition="all 0.2s"
            mt={2}
          >
            Realizar Check-in
          </Button>
        </VStack>
      </MotionBox>

      <Text mt={8} fontSize="sm" color="whiteAlpha.600" textAlign="center">
        © 2025 Barbearia Freire • Todos os direitos reservados
      </Text>
    </MotionBox>
  );
};

export default MobileCheckinPage;
